import { useEffect, useMemo, useState } from "react";
import { getImageUrl, getYoutubeThumbnailUrl } from "../../../utils/media";
import "./style.css";

const DEFAULT_TABS = [
  { key: "parceiros-institucionais", label: "Parceiros Institucionais" },
  { key: "parceiros-em-projetos", label: "Parcerias em Projetos" },
  { key: "patrocinio", label: "Patrocínios" },
  { key: "apoiadores", label: "Apoiadores" },
];

const CATEGORY_LABELS = Object.fromEntries(
  DEFAULT_TABS.map((tab) => [tab.key, tab.label]),
);

function getTabs(categories) {
  if (!Array.isArray(categories) || categories.length === 0) {
    return DEFAULT_TABS;
  }

  return categories.map((category) => ({
    key: category,
    label: CATEGORY_LABELS[category] || category,
  }));
}

export default function Parceiros({
  section,
  categories,
  partners,
  isLoading,
  hasLoadError,
}) {
  const tabs = useMemo(() => getTabs(categories), [categories]);
  const [activeTab, setActiveTab] = useState(DEFAULT_TABS[0].key);

  useEffect(() => {
    if (!tabs.some((tab) => tab.key === activeTab)) {
      setActiveTab(tabs[0]?.key || "");
    }
  }, [activeTab, tabs]);

  const lista = useMemo(() => {
    if (!Array.isArray(partners)) {
      return [];
    }

    return partners.filter((partner) => partner.category === activeTab);
  }, [activeTab, partners]);

  return (
    <section className="parceiros">
      <div className="parceiros__cabecalho">
        <h3 className="parceiros__titulo-secao">
          {section?.title || "Parceiros"}
        </h3>
        <nav className="parceiros__tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`parceiros__tab ${activeTab === tab.key ? "parceiros__tab--ativo" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {isLoading ? (
        <p className="parceiros__vazio">Carregando...</p>
      ) : hasLoadError ? (
        <p className="parceiros__vazio">
          Não foi possível carregar os parceiros.
        </p>
      ) : lista.length === 0 ? (
        <p className="parceiros__vazio">
          Ainda não há parceiros nessa categoria.
        </p>
      ) : (
        <div className="parceiros__grid">
          {lista.map((partner, index) => {
            const videoThumbnailUrl = getYoutubeThumbnailUrl(partner.redirectUrl);
            const logoUrl = getImageUrl(partner.logoUrl) || videoThumbnailUrl;
            const logo = logoUrl ? (
              <>
                <img
                  src={logoUrl}
                  alt={partner.name}
                  onError={(event) => {
                    if (videoThumbnailUrl && event.currentTarget.src !== videoThumbnailUrl) {
                      event.currentTarget.src = videoThumbnailUrl;
                      return;
                    }
                    event.currentTarget.style.display = "none";
                    event.currentTarget.nextElementSibling.style.display = "inline";
                  }}
                />
                <span className="parceiros__logo-fallback">{partner.name}</span>
              </>
            ) : (
              <span>{partner.name}</span>
            );

            return partner.redirectUrl ? (
              <a
                key={partner._id || index}
                href={partner.redirectUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="parceiros__logo"
                aria-label={partner.name}
              >
                {logo}
              </a>
            ) : (
              <div key={partner._id || index} className="parceiros__logo">
                {logo}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
